import { useEffect, useState, useCallback } from 'react';
import Plyr from 'plyr';
import { isEqual } from 'lodash';

const useVideoPlayer = (videoRef, config) => {
  const [videoPlayer, setVideoPlayer] = useState(null);

  const [playerState, setPlayerState] = useState({
    isPlaying: null,
    isPaused: null,
    volume: null,
    playbackSpeed: null,
    isFinished: null,
    quality: null,
  });

  const [isReady, setIsReady] = useState();
  const [videoSource, setVideoSource] = useState();

  const updatePlayerState = (player) => {
    setPlayerState((oldState) => {
      const newState = {
        ...oldState,
        isPlaying: player.playing,
        isPaused: player.paused,
        isFinished: player.ended,
        volume: player.volume,
        quality: player.quality,
        playbackSpeed: player.speed,
      };

      return isEqual(oldState, newState) ? oldState : newState;
    });
  };

  const registerEventListeners = useCallback((player) => {
    if (!player) return;

    player.on('playing', (event) => {
      updatePlayerState(event.detail.plyr);
    });
    player.on('play', (event) => {
      updatePlayerState(event.detail.plyr);
    });
    player.on('pause', (event) => {
      updatePlayerState(event.detail.plyr);
    });
    player.on('volumechange', (event) => {
      updatePlayerState(event.detail.plyr);
    });
    player.on('ratechange', (event) => {
      updatePlayerState(event.detail.plyr);
    });
    player.on('ended', (event) => {
      updatePlayerState(event.detail.plyr);
    });
    player.on('qualitychange', (event) => {
      updatePlayerState(event.detail.plyr);
    });

    // Set initial state when player is ready
    player.on('ready', (event) => {
      updatePlayerState(event.detail.plyr);
      setIsReady(true);
    });
  }, []);

  const unregisterEventListeners = (player) => {
    if (!player) return;

    player.off('playing');
    player.off('play');
    player.off('pause');
    player.off('volumechange');
    player.off('ratechange');
    player.off('ended');
    player.off('qualitychange');
    player.off('ready');
  };

  useEffect(() => {
    if (videoPlayer) {
      videoPlayer.source = videoSource;
    }
  }, [videoPlayer, videoSource]);

  useEffect(() => {
    let player;
    if (videoRef.current) {
      player = new Plyr(videoRef.current, config);
      setVideoPlayer(player);
      registerEventListeners(player);
    }

    return () => {
      unregisterEventListeners(player);
    };
  }, [videoRef, config, registerEventListeners]);

  const togglePlay = useCallback(() => {
    videoPlayer.togglePlay();
  }, [videoPlayer]);

  const play = useCallback(() => {
    videoPlayer.play();
  }, [videoPlayer]);

  const pause = useCallback(() => {
    videoPlayer.pause();
  }, [videoPlayer]);

  const setVolume = useCallback(
    (value) => {
      videoPlayer.volume = value;
    },
    [videoPlayer],
  );

  const setQuality = useCallback(
    (value) => {
      videoPlayer.quality = value;
    },
    [videoPlayer],
  );

  const setPlaybackSpeed = useCallback(
    (value) => {
      videoPlayer.speed = value;
    },
    [videoPlayer],
  );

  const setSource = useCallback((src) => {
    setVideoSource({
      type: 'video',
      sources: [typeof src === 'object' ? src : { src }],
    });
  }, []);

  const setSources = useCallback((sources) => {
    setVideoSource({
      type: 'video',
      sources: sources.map((src) => (typeof src === 'object' ? src : { src })),
    });
  }, []);

  return {
    ...playerState,
    play,
    pause,
    setVolume,
    setPlaybackSpeed,
    togglePlay,
    setQuality,
    setSource,
    setSources,
    isReady,
  };
};

export default useVideoPlayer;
